import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { countTickets, getTicketsPage } from "../../services/dataService";
import { isMissingIndexError } from "../../services/firestoreErrors";
import { INBOX_VIEWS, inboxEmptyText, inboxHeadLine, raisedByCell } from "../../utils/ticketDetail";
import { showingOfLabel } from "../../utils/kitPresentation";
import { ListHead } from "../../components/kit/PageHead";
import { TableCard } from "../../components/kit/SectionCard";
import { PersonCell, SkeletonRows, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import Pager from "../../components/kit/Pager";
import ViewChips from "../../components/kit/ViewChips";
import { useUsersByIds } from "../Students/tabs/useUsersByIds";
import TicketRow from "./TicketRow";

// The Inbox (047 D55, D56), drawn from the Tickets table (10810:61045, 10821:93710): every ticket
// of one status, newest first, 25 to a page on the (status, raisedAt desc) index, and each status
// chip's count from one aggregation when the page opens. The raisers' names come from one read
// per person. A row opens the ticket page (D54). Nothing here reads a whole collection.

const PAGE_SIZE = 25;
const COLUMNS = ["Ticket", "Reason", "Raised by", "Status", "Last activity", "Actions"];
const MISSING_INDEX = "This view needs a Firestore index that is missing or still building.";

// The RAISED BY cell: the person, or "System" for a ticket nobody raised.
const RaisedBy = ({ cell }) =>
  cell.system ? <TwoLineCell top={cell.name} bottom={cell.sub} /> : <PersonCell name={cell.name} colorKey={cell.id} sub={cell.sub} />;

const InboxList = () => {
  const [status, setStatus] = useState("open");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(""); // the error block's line; "" when the page loaded
  const [counts, setCounts] = useState({}); // status -> its count, for the chips; null where it failed
  // startAfter cursors: cursors.current[p] is the last document of page p.
  const cursors = useRef([]);
  const loadToken = useRef(0); // the newest load wins; older ones drop their result
  const wanted = useRef(0); // the page the newest load asked for, so Retry asks for it again
  const users = useUsersByIds(rows.map((t) => t.raisedBy));

  // Every chip's count, once per visit (D56), each failing alone: a chip whose count failed shows
  // no number.
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      INBOX_VIEWS.map((view) =>
        countTickets(view.key).catch((e) => {
          console.error("Error counting tickets:", e);
          return null;
        })
      )
    ).then((values) => {
      if (!cancelled) setCounts(Object.fromEntries(INBOX_VIEWS.map((view, i) => [view.key, values[i]])));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPage = useCallback(
    async (nextPage) => {
      const token = ++loadToken.current;
      wanted.current = nextPage;
      setLoading(true);
      try {
        const cursor = nextPage === 0 ? null : cursors.current[nextPage - 1] || null;
        const data = await getTicketsPage(status, { pageSize: PAGE_SIZE, cursor });
        if (token !== loadToken.current) return;
        cursors.current[nextPage] = data.cursor;
        setRows(data.rows);
        setHasMore(data.hasMore);
        setPage(nextPage);
        setError("");
      } catch (e) {
        if (token !== loadToken.current) return;
        console.error("Error loading tickets:", e);
        // Error State 10618:2251: always say whether anything was changed.
        setError(isMissingIndexError(e) ? `${MISSING_INDEX} Nothing has been changed.` : "Nothing has been changed.");
      } finally {
        if (token === loadToken.current) setLoading(false);
      }
    },
    [status]
  );

  // A new status starts again from page 1 with fresh cursors.
  useEffect(() => {
    cursors.current = [];
    setPage(0);
    setHasMore(false);
    loadPage(0);
  }, [loadPage]);

  // Next follows the page read alone (`pageOf` reads one row more than it shows): the counts are
  // read once per visit (D56), and a count gone stale must not hide a ticket that arrived since.
  const total = counts[status];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <ListHead caption="Support" title="Inbox" subtitle={inboxHeadLine({ total, loading: !(status in counts) })} />

      <ViewChips caption="Status" views={INBOX_VIEWS} value={status} counts={counts} onChange={setStatus} />

      <TableCard
        footer={
          <Pager
            label={loading || error ? "" : showingOfLabel(page, rows.length, PAGE_SIZE, total)}
            page={page}
            hasMore={hasMore}
            onPage={loadPage}
          />
        }
      >
        <TableContainer>
          <Table aria-busy={loading}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={COLUMNS.length} />
              ) : error || rows.length === 0 ? (
                <TableMessageRow
                  colSpan={COLUMNS.length}
                  error={error ? "Could not load tickets" : ""}
                  errorMessage={error}
                  onRetry={() => loadPage(wanted.current)}
                  emptyText={inboxEmptyText(status)}
                />
              ) : (
                rows.map((t) => <TicketRow key={t.id} ticket={t} raisedBy={<RaisedBy cell={raisedByCell(t, users)} />} openOnClick />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>
    </Box>
  );
};

// An old link, /inbox?ticket=<id> (bookmarks, the old Queue rows), opens the ticket page in its
// place (D54) before the list starts a read, and leaves no history entry behind.
const Inbox = () => {
  const [searchParams] = useSearchParams();
  const wanted = searchParams.get("ticket");
  return wanted ? <Navigate to={`/inbox/${encodeURIComponent(wanted)}`} replace /> : <InboxList />;
};

export default Inbox;
